import { InlineCellMenu } from './InlineCellMenu.tsx'
import type { User } from '../../lib/types.ts'
import { SearchField } from '../../ui/SearchField.tsx'
import styles from './table.module.css'

export interface TableFiltersState {
  q: string
  assigneeId: number | null
  label: string | null
}

/** Sentinel for "no filter". A string, so it can never collide with a numeric id. */
const ALL = '__all__'

export const EMPTY_FILTERS: TableFiltersState = {
  q: '',
  assigneeId: null,
  label: null,
}

export function hasActiveFilters(filters: TableFiltersState): boolean {
  return filters.q !== '' || filters.assigneeId !== null || filters.label !== null
}

/** ui-spec §5.3. Search, person, label, archive toggle, then the count pinned to the right. */
export function TableToolbar({
  filters,
  users,
  labels,
  shown,
  total,
  onChange,
}: {
  filters: TableFiltersState
  users: User[]
  labels: string[]
  shown: number
  total: number
  onChange: (next: TableFiltersState) => void
}): React.JSX.Element {
  const assignee = users.find((u) => u.id === filters.assigneeId)

  return (
    <div className={styles.toolbar}>
      <SearchField
        scope="view"
        className={styles.search}
        value={filters.q}
        onChange={(q) => onChange({ ...filters, q })}
        placeholder="Szukaj w zadaniach"
        label="Szukaj w zadaniach"
      />

      {assignee && (
        <span className={styles.chip}>
          {assignee.displayName}
          <button
            type="button"
            className={styles.chipClear}
            onClick={() => onChange({ ...filters, assigneeId: null })}
            aria-label="Wyczyść filtr osoby"
          >
            ✕
          </button>
        </span>
      )}

      {filters.label && (
        <span className={styles.chip}>
          {filters.label}
          <button
            type="button"
            className={styles.chipClear}
            onClick={() => onChange({ ...filters, label: null })}
            aria-label="Wyczyść filtr labelki"
          >
            ✕
          </button>
        </span>
      )}

      <InlineCellMenu
        label="Filtruj po osobie"
        trigger={<span className={styles.control}>Osoba ▾</span>}
        value={filters.assigneeId ?? -1}
        options={[
          { value: -1, label: 'wszyscy' },
          ...users.map((u) => ({ value: u.id, label: u.displayName })),
        ]}
        onSelect={(value) =>
          onChange({ ...filters, assigneeId: value === -1 ? null : Number(value) })
        }
      />

      <InlineCellMenu
        label="Filtruj po labelce"
        trigger={<span className={styles.control}>Labelka ▾</span>}
        value={filters.label ?? ALL}
        options={[
          { value: ALL, label: 'wszystkie' },
          ...labels.map((l) => ({ value: l, label: l })),
        ]}
        onSelect={(value) => onChange({ ...filters, label: value === ALL ? null : String(value) })}
      />

      {/* Nie ma tu przycisku „Archiwum". Zamknięta praca dostała własną zakładkę — filtr, który
          podmieniał zawartość tej samej tabeli, ukrywał przed człowiekiem, na co właściwie patrzy. */}

      <span className={styles.count}>
        {shown} / {total} zadań
      </span>
    </div>
  )
}
